import { ColDef } from "@ag-grid-community/core";
import { inject, Injectable } from "@angular/core";
import {
  AlgoMode,
  CampaignType,
  StrategyStateEnum,
  TacosStrategyCreateRequest,
  TacosStrategyGroup,
} from "@front/m19-api-client";
import { hasFieldToUpdateTacos } from "@front/m19-models";
import {
  AccountSelectionService,
  AsinService,
  Constant,
  SpStrategyService,
  TacosStrategiesService,
  TacosStrategy,
} from "@front/m19-services";
import {
  BulkUploadPreviewStatus,
  BulkUploadRunStatus,
} from "@m19-board/bulk-upload/bulk-upload-grid-cells/bulk-upload-type-icon.component";
import {
  BulkImportService,
  TacosStrategyExport,
} from "@m19-board/strategies/strategy-bulk-upload-modal/bulk-import.service";
import {
  BehaviorSubject,
  catchError,
  forkJoin,
  isObservable,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from "rxjs";
import {
  BulkUploadBuckets,
  BulkUploadFieldPreview,
  BulkUploadHandler,
  BulkUploadProgressCounter,
  BulkUploadRowPreview,
  BulkUploadRunResultItem,
  rowHasFieldError,
  rowIsUnchanged,
} from "../bulk-upload-context.service";
import { BulkUploadGridUtilsService } from "../bulk-upload-grid-utils.service";

export type TacosBulkUploadEntity = TacosStrategyExport & { strategyId?: number };

type BulkFieldValidator<T> = (entity: Partial<T>) => string | Observable<string | null> | null;

@Injectable()
export class TacosBulkUploadHandler implements BulkUploadHandler<TacosBulkUploadEntity> {
  private readonly bulkImportService = inject(BulkImportService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly tacosStrategiesService = inject(TacosStrategiesService);
  private readonly asinService = inject(AsinService);
  private readonly spStrategyService = inject(SpStrategyService);
  private readonly bulkUploadGridUtilsService = inject(BulkUploadGridUtilsService);

  protected readonly seenStrategyIdsDuringPreview = new Set<number>();

  private readonly bulkConfig = this.bulkImportService.TacosStrategyBulkConfig;

  public readonly expectedHeaders: string[] = this.bulkConfig.map((field) => field.csvField.header);

  private readonly progress = new BehaviorSubject<BulkUploadProgressCounter>({ completed: 0, total: 0 });
  public readonly progress$ = this.progress.asObservable();

  protected readonly catalog$ = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) => this.asinService.getCatalog(am.accountId, am.marketplace)),
  );

  protected readonly tacosStrategies$ = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) => this.tacosStrategiesService.getAllTacosStrategies(am.accountId, am.marketplace)),
  );

  protected readonly unavailableAsins$ = this.tacosStrategiesService.unavailableAsinsForTacos$;

  buildPreview(rows: string[]): Observable<BulkUploadBuckets<TacosBulkUploadEntity>> {
    this.seenStrategyIdsDuringPreview.clear();
    if (rows.length === 0) return of([]);
    return forkJoin(rows.map((row) => this.parseRow(row.split(/\t/)))).pipe(
      map((parsed) => parsed.filter((row): row is BulkUploadRowPreview<TacosBulkUploadEntity> => row !== null)),
    );
  }

  run(buckets: BulkUploadBuckets<TacosBulkUploadEntity>): Observable<BulkUploadRunResultItem<TacosBulkUploadEntity>[]> {
    const creations = buckets.filter((b) => b.status === BulkUploadPreviewStatus.CREATION);
    const updates = buckets.filter((b) => b.status === BulkUploadPreviewStatus.UPDATE);
    this.progress.next({ completed: 0, total: creations.length + updates.length });
    if (creations.length === 0 && updates.length === 0) return of([]);

    const requests = [
      ...creations.map((row) => this.buildCreateRequest(row)),
      ...updates.map((row) => this.buildUpdateRequest(row)),
    ];
    return forkJoin(requests);
  }

  getPreviewGridColumnsDefs(): ColDef<BulkUploadRowPreview<TacosBulkUploadEntity>>[] {
    return this.bulkUploadGridUtilsService.getPreviewGridColumnsDefs(this.bulkConfig, "name");
  }

  getRunResultGridColumnsDefs(): ColDef<BulkUploadRunResultItem<TacosBulkUploadEntity>>[] {
    const withoutName = this.bulkConfig.filter((field) => field.field !== "name");
    return this.bulkUploadGridUtilsService.getRunResultGridColumnsDefs(withoutName, "name", "strategyId");
  }

  export(): void {
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        take(1),
        switchMap((am) =>
          this.tacosStrategiesService
            .getAllTacosStrategies(am.accountId, am.marketplace)
            .pipe(map((strategies) => ({ am, strategies }))),
        ),
      )
      .subscribe(({ am, strategies }) => {
        this.bulkImportService.exportTacosStrategyCsv(
          am,
          strategies.map((s) => this.toExportRow(s)),
        );
      });
  }

  private parseRow(fields: string[]): Observable<BulkUploadRowPreview<TacosBulkUploadEntity> | null> {
    const parsed = this.bulkImportService.parseTacosCsvRow(fields);
    if (!parsed) {
      return of({
        parsedEntity: {},
        fieldsPreview: this.bulkConfig.map((field, i) => ({
          field: field.field,
          providedValue: fields[i] ?? "",
          parsedValue: undefined,
          error: i === 0 ? "Wrong line format" : undefined,
          isCreation: false,
          hasChanged: false,
        })),
        status: BulkUploadPreviewStatus.VALUE_ERROR,
      });
    }

    const partial: Partial<TacosBulkUploadEntity> = { ...parsed };
    const isCreation = partial.tacosStrategyGroupId === 0;

    return forkJoin(
      this.bulkConfig.map((field) => {
        const error = tacosFieldValidators[field.field]?.bind(this)(partial) ?? null;
        return isObservable(error) ? error : of(error);
      }),
    ).pipe(
      withLatestFrom(this.tacosStrategies$.pipe(take(1))),
      map(([errors, tacosStrategies]) => {
        const existing = !isCreation
          ? tacosStrategies.find((s) => s.tacosStrategyGroupId === partial.tacosStrategyGroupId)
          : undefined;
        const fieldsPreview: BulkUploadFieldPreview<TacosBulkUploadEntity>[] = errors.map((error, i) => {
          const field = this.bulkConfig[i].field;
          const existingValue = this.getExistingFieldValue(field, existing);
          const parsedValue = partial[field];
          return {
            field,
            providedValue: fields[i] ?? "",
            parsedValue,
            error: error ?? undefined,
            isCreation,
            existingValue,
            hasChanged: isCreation || !existing ? false : this.hasFieldChanged(field, existing, partial),
          };
        });
        return {
          parsedEntity: partial,
          fieldsPreview,
          status: this.getUploadRowPreviewStatus(partial, fieldsPreview),
        };
      }),
    );
  }

  private getExistingFieldValue(
    field: keyof TacosBulkUploadEntity,
    existing: TacosStrategy | undefined,
  ): TacosBulkUploadEntity[keyof TacosBulkUploadEntity] | undefined {
    if (!existing) return undefined;
    switch (field) {
      case "tacosStrategyGroupId":
        return existing.tacosStrategyGroupId;
      case "name":
        return existing.spStrategy?.name;
      case "state":
        return existing.state;
      case "algoMode":
        return existing.spStrategy?.algoMode;
      case "tacosTarget":
        return existing.tacosTarget;
      case "asins":
        return existing.spStrategy?.asins?.map((a) => a.asin!) ?? [];
      default:
        return undefined;
    }
  }

  private hasFieldChanged(
    field: keyof TacosBulkUploadEntity,
    existing: TacosStrategy,
    parsed: Partial<TacosBulkUploadEntity>,
  ): boolean {
    if (field === "tacosStrategyGroupId" || field === "algoMode" || field === "strategyId") return false;
    if (field === "asins") {
      return this.hasAsinsChanged(existing, parsed);
    }
    return this.getExistingFieldValue(field, existing) !== parsed[field];
  }

  private hasAsinsChanged(existing: TacosStrategy, parsed: Partial<TacosBulkUploadEntity>): boolean {
    const existingAsins = existing.spStrategy?.asins?.map((a) => a.asin!) ?? [];
    const parsedAsins = parsed.asins ?? [];
    if (existingAsins.length !== parsedAsins.length) return true;
    const existingSet = new Set(existingAsins);
    return parsedAsins.some((a) => !existingSet.has(a));
  }

  private getUploadRowPreviewStatus(
    partial: Partial<TacosBulkUploadEntity>,
    fieldsPreview: BulkUploadFieldPreview<TacosBulkUploadEntity>[],
  ): BulkUploadPreviewStatus {
    if (rowHasFieldError(fieldsPreview)) return BulkUploadPreviewStatus.VALUE_ERROR;
    if (partial.tacosStrategyGroupId === 0) return BulkUploadPreviewStatus.CREATION;
    if (rowIsUnchanged(fieldsPreview)) return BulkUploadPreviewStatus.UNCHANGED;
    return BulkUploadPreviewStatus.UPDATE;
  }

  private buildCreateRequest(
    row: BulkUploadRowPreview<TacosBulkUploadEntity>,
  ): Observable<BulkUploadRunResultItem<TacosBulkUploadEntity>> {
    const body: TacosStrategyCreateRequest = {
      name: row.parsedEntity.name!,
      tacosTarget: row.parsedEntity.tacosTarget!,
      asins: row.parsedEntity.asins ?? [],
      state: row.parsedEntity.state,
    };
    return this.tacosStrategiesService.createTacosStrategyGroup(body).pipe(
      map((ts) => this.mapGroupToRunResult(ts, row.parsedEntity, BulkUploadRunStatus.CREATED)),
      catchError((err) => of(this.mapErrorToRunResult(row.parsedEntity, err))),
      tap(() => this.incrementProgress()),
    );
  }

  private buildUpdateRequest(
    row: BulkUploadRowPreview<TacosBulkUploadEntity>,
  ): Observable<BulkUploadRunResultItem<TacosBulkUploadEntity>> {
    return this.tacosStrategies$.pipe(
      take(1),
      withLatestFrom(this.accountSelectionService.singleAccountMarketplaceSelection$),
      switchMap(([tacosStrategies, am]) => {
        const existing = tacosStrategies.find((s) => s.tacosStrategyGroupId === row.parsedEntity.tacosStrategyGroupId);
        if (!existing?.spStrategy) {
          return of(this.mapErrorToRunResult(row.parsedEntity, "Strategy does not exist"));
        }

        const updateParams = this.bulkImportService.getTacosStrategyUpdateParams(
          row.parsedEntity,
          existing,
          am.resourceOrganizationId!,
        );
        const asinDiff = this.getAsinDiff(row.parsedEntity.asins ?? [], existing);
        updateParams.asinsToAdd = asinDiff.toAdd;
        updateParams.asinsToDelete = asinDiff.toDelete;

        if (!hasFieldToUpdateTacos(updateParams)) {
          return of(this.mapErrorToRunResult(row.parsedEntity, "Nothing to update"));
        }

        return this.tacosStrategiesService.updateTacosStrategyGroup(updateParams).pipe(
          switchMap((ts) =>
            this.spStrategyService.addAsinsToStrategy(existing.spStrategy!, asinDiff.toAdd).pipe(
              switchMap((s) => this.spStrategyService.deleteAsinsFromStrategy(s, asinDiff.toDelete)),
              map(() => ts),
            ),
          ),
          map((ts) => this.mapGroupToRunResult(ts, row.parsedEntity, BulkUploadRunStatus.UPDATED)),
          catchError((err) => of(this.mapErrorToRunResult(row.parsedEntity, err))),
        );
      }),
      tap(() => this.incrementProgress()),
    );
  }

  private getAsinDiff(asins: string[], existing: TacosStrategy): { toAdd: string[]; toDelete: string[] } {
    const existingAsins = existing.spStrategy?.asins?.map((a) => a.asin!) ?? [];
    const toAdd = asins.filter((a) => !existingAsins.includes(a));
    const toDelete = existingAsins.filter((a) => !asins.includes(a));
    return { toAdd, toDelete };
  }

  private mapGroupToRunResult(
    ts: TacosStrategyGroup,
    parsedEntity: Partial<TacosBulkUploadEntity>,
    status: BulkUploadRunStatus.CREATED | BulkUploadRunStatus.UPDATED,
  ): BulkUploadRunResultItem<TacosBulkUploadEntity> {
    return {
      parsedEntity,
      resultEntity: {
        tacosStrategyGroupId: ts.tacosStrategyGroupId,
        name: ts.spStrategy?.name ?? parsedEntity.name!,
        algoMode: ts.spStrategy?.algoMode ?? AlgoMode.TACOS_TARGET,
        asins: ts.spStrategy?.asins?.map((a) => a.asin!) ?? parsedEntity.asins ?? [],
        tacosTarget: ts.tacosTarget ?? parsedEntity.tacosTarget!,
        state: ts.state ?? parsedEntity.state!,
        strategyId: ts.spStrategyId,
      },
      status,
    };
  }

  private mapErrorToRunResult(
    parsedEntity: Partial<TacosBulkUploadEntity>,
    err: unknown,
  ): BulkUploadRunResultItem<TacosBulkUploadEntity> {
    return {
      parsedEntity,
      resultEntity: null,
      error: typeof err === "string" ? err : ((err as { message?: string })?.message ?? "error"),
      status: BulkUploadRunStatus.FAILED,
    };
  }

  private incrementProgress(): void {
    const current = this.progress.value;
    this.progress.next({ ...current, completed: current.completed + 1 });
  }

  private toExportRow(ts: TacosStrategy): TacosStrategyExport {
    const campaignTypes: CampaignType[] = [];
    if (ts.sbStrategy) campaignTypes.push(CampaignType.SB);
    if (ts.sdStrategy) campaignTypes.push(CampaignType.SD);
    return {
      tacosStrategyGroupId: ts.tacosStrategyGroupId,
      name: ts.spStrategy?.name ?? "",
      algoMode: ts.spStrategy?.algoMode ?? AlgoMode.TACOS_TARGET,
      asins: ts.spStrategy?.asins?.map((a) => a.asin!) ?? [],
      tacosTarget: ts.tacosTarget!,
      state: ts.state!,
      campaignTypes,
    };
  }
}

const tacosFieldValidators: Partial<Record<keyof TacosBulkUploadEntity, BulkFieldValidator<TacosBulkUploadEntity>>> = {
  tacosStrategyGroupId: function (this: TacosBulkUploadHandler, entity) {
    if (entity.tacosStrategyGroupId === undefined || Number.isNaN(entity.tacosStrategyGroupId)) {
      return "Strategy ID is not a number or is missing";
    }
    if (entity.tacosStrategyGroupId === 0) return null;
    return this.tacosStrategies$.pipe(
      take(1),
      map((strategies) => {
        if (!strategies.some((s) => s.tacosStrategyGroupId === entity.tacosStrategyGroupId)) {
          return "Strategy does not exist";
        }
        if (this.seenStrategyIdsDuringPreview.has(entity.tacosStrategyGroupId!)) {
          return "Duplicated strategy update";
        }
        this.seenStrategyIdsDuringPreview.add(entity.tacosStrategyGroupId!);
        return null;
      }),
    );
  },
  state: function (entity) {
    if (entity.state !== StrategyStateEnum.ENABLED && entity.state !== StrategyStateEnum.PAUSED) {
      return "Strategy state is not valid";
    }
    return null;
  },
  name: function (this: TacosBulkUploadHandler, entity) {
    if (!entity.name) return entity.tacosStrategyGroupId === 0 ? "Name is required" : null;
    return this.tacosStrategies$.pipe(
      take(1),
      map((strategies) => {
        const existing = strategies.find((s) => s.tacosStrategyGroupId === entity.tacosStrategyGroupId);
        if (existing?.spStrategy?.name === entity.name) return null;
        // a tacos strategy drives SP, SB and SD campaigns: its name must be valid for all three
        if (!Constant.nameRegexp.test(entity.name!) || Constant.invalidSBSDNameRegexp.test(entity.name!)) {
          return "Invalid character in strategy name";
        }
        return null;
      }),
    );
  },
  tacosTarget: function (entity) {
    if (entity.tacosStrategyGroupId === 0 && (entity.tacosTarget === undefined || Number.isNaN(entity.tacosTarget))) {
      return "Tacos target is not valid";
    }
    return null;
  },
  asins: function (this: TacosBulkUploadHandler, entity) {
    return forkJoin([
      this.catalog$.pipe(take(1)),
      this.unavailableAsins$.pipe(take(1)),
      this.tacosStrategies$.pipe(take(1)),
    ]).pipe(
      map(([catalog, unavailable, strategies]) => {
        if (!entity.asins?.length) return "Asins are not valid";
        const missing = entity.asins.filter((a) => !catalog.asinOffers.has(a));
        if (missing.length > 0) return `The following ASINs are not in catalog: ${missing.join(", ")}`;

        if (entity.tacosStrategyGroupId === 0) {
          if (entity.asins.some((a) => unavailable.has(a))) {
            return "Some ASINs are already used by other strategies";
          }
          return null;
        }

        const existing = strategies.find((s) => s.tacosStrategyGroupId === entity.tacosStrategyGroupId);
        const existingAsins = existing?.spStrategy?.asins?.map((a) => a.asin!) ?? [];
        const toAdd = entity.asins.filter((a) => !existingAsins.includes(a));
        if (toAdd.some((a) => unavailable.has(a))) {
          return "Some ASINs are already used by other strategies";
        }
        return null;
      }),
    );
  },
};
